import { HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FileUploadEvent } from 'primeng/fileupload';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AuthRoles } from 'src/app/auth/auth.types';
import { AdminService } from 'src/app/services/admin.service';
import { AttachmentService } from 'src/app/services/attachment.service';
import { LoaderService } from 'src/app/services/loader.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppUtils } from 'src/app/shared/app.utils';
import { ApplicationAttachment } from 'src/app/shared/types/attachment.types';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-bulk-imports',
  templateUrl: './bulk-imports.component.html'
})
export class BulkImportsComponent implements OnDestroy, OnInit {
  apiRoot: string = environment.apiEndpoint;
  cols: any = [];
  customHeaders: HttpHeaders = new HttpHeaders({
    'Skip-Loader': 'true',
    'Authorization': 'Bearer ' + sessionStorage.getItem(environment.authTokenStorageKey)
  });
  fileTransferSucceeded: boolean;
  hasAdmin: AuthRoles[] = ['NESS Administrator'];
  importStatusMessage: string = 'Import processing started.';
  importedUserResults: ServerModels.ENESS_ImportedUsers[];
  rows: number = AppConstants.defaultTableRows;
  private readonly attachmentType: ApplicationAttachment = 'BulkUserImportTemplate';
  userImportTemplate$: Observable<ServerModels.ENESS_ApplicationAttachment> = this.attachmentService.getAttachmentByType(this.attachmentType);
  @ViewChild('downloadTemplateLink', { static: false }) private downloadTemplateLink: ElementRef;

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private adminService: AdminService,
    private loaderService: LoaderService,
    private attachmentService: AttachmentService,
    public elementRef: ElementRef) { }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngOnInit() {
    this.cols = [
      { field: 'FirstName', header: 'FirstName' },
      { field: 'LastName', header: 'LastName' },
      { field: 'UserType', header: 'User Type' },
      { field: 'FieldDivisionName', header: 'Field Division Name' },
      { field: 'AgencyName', header: 'Agency Name' },
      { field: 'AgencyState', header: 'Agency State' },
      { field: 'Status', hoverfield: 'Errors', header: 'Status' },
      { field: 'ItemUploadDateTime', header: 'Import Time' },
    ];

    this.getImportedResult();
  }

  public onUploadHandler(event: FileUploadEvent): void {
    this.fileTransferSucceeded = event.originalEvent['status'] == 200;
    this.importStatusMessage = "Import completed";
    this.refreshStatus();
  }

  public onSendHandler(): void {
    // temporary fix to prevent uploading same document multiple times D-15845 because "disabled" attribute on
    // p - upload does not affect "Upload" button, and messing with "files" causes display to show either
    // more or less files than user is actually uploading
    let uploadButtonElement = document.getElementById('BulkUserUpload').querySelector('div div p-button button');
    if (uploadButtonElement) {
      uploadButtonElement.setAttribute('disabled', 'true');
    }
  }

  public refreshStatus(): void {
    this.getImportedResult();
  }

  private getImportedResult() {
    this.adminService.getImportedUsers().pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (result: ServerModels.ENESS_ImportedUsers[]) => this.importedUserResults = result
    });
  }

  onClickDownload(importTemplate: ServerModels.ENESS_ApplicationAttachment) {
    if (!AppUtils.objectIsEmpty(importTemplate)) {
      const blob = AppUtils.base64toBlob(importTemplate.Attachment, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      const link = this.downloadTemplateLink.nativeElement;
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }
}
